import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { projectPermission } from '../../utils/permissions';
import { toast } from 'react-toastify';
import { fetchProjectById, fetchProjectMembers, fetchProjectStats } from '../../store/slices/projectsSlice';
import { fetchSprints, fetchActiveSprint } from '../../store/slices/sprintsSlice';
import ProjectBacklog from './ProjectBacklog';
import ProjectBoard from './ProjectBoard';
import ProjectSettings from './ProjectSettings';
import KnowledgeCenter from './KnowledgeCenter';
import AllWork from './AllWork';
import AllIssues from './AllIssues';
import ExecutivePlan from './ExecutivePlan';
import Modal from '../../components/common/Modal';
import TaskDetailSidePanel from '../tasks/TaskDetailSidePanel';
import { generateAndDownloadReport } from '../../services/reporting';
import { fetchTasks, clearTasks } from '../../store/slices/tasksSlice';
import './ProjectPage.css';

const ProjectPage = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentProject, members, loading } = useSelector((state) => state.projects);
  const { activeSprint, sprints } = useSelector((state) => state.sprints);

  // Determine active tab from URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/board')) return 'board';
    if (path.includes('/knowledge')) return 'knowledge';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/allwork')) return 'allwork';
    if (path.includes('/allissues')) return 'allissues';
    if (path.includes('/executive-plan')) return 'executive';
    return 'backlog'; // default
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());
  const [sidePanelTask, setSidePanelTask] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportScope, setReportScope] = useState('project');
  const [reportSprintId, setReportSprintId] = useState('');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportGenerating, setReportGenerating] = useState(false);

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  // Clear stale tasks immediately when switching projects
  useEffect(() => {
    dispatch(clearTasks());
  }, [dispatch, projectId]);

  // Load initial project data
  useEffect(() => {
    dispatch(fetchProjectById(projectId));
    dispatch(fetchProjectMembers(projectId));
    dispatch(fetchProjectStats(projectId));
    dispatch(fetchSprints({ projectId }));
    dispatch(fetchActiveSprint(projectId));
  }, [dispatch, projectId]);

  // Track recently visited projects per user for Dashboard "Recent Projects"
  useEffect(() => {
    if (!currentProject?.id || !user?.id) return;
    const key = `recent-project-ids-${user.id}`;
    try {
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const updated = [currentProject.id, ...existing.filter(id => id !== currentProject.id)].slice(0, 10);
      localStorage.setItem(key, JSON.stringify(updated));
    } catch {
      // ignore localStorage errors
    }
  }, [currentProject?.id, user?.id]);

  // viewer is always read-only regardless of project membership
  const isViewer = user?.role === 'viewer';

  // Project role takes precedence over system role within a project.
  // systemFallbackRoles only apply when the user has no explicit project membership.
  const canManageProject = projectPermission(user, members, {
    projectRoles: ['admin', 'manager', 'member'],
    systemFallbackRoles: ['admin', 'manager'],
  });

  const canViewSettings = projectPermission(user, members, {
    projectRoles: ['admin', 'manager'],
    systemFallbackRoles: ['admin', 'manager'],
  });

  const canGenerateFullReport = projectPermission(user, members, {
    projectRoles: ['admin', 'manager', 'member'],
    systemFallbackRoles: ['admin'],
  });

  // Side panel: open a task without navigating away
  const handleOpenTaskSidePanel = (taskIdentifier) => {
    setSidePanelTask(taskIdentifier);
  };

  const handleCloseSidePanel = () => {
    setSidePanelTask(null);
    dispatch(fetchTasks({ projectId, pageSize: 5000 }));
  };

  const handleTaskDeletedInPanel = () => {
    setSidePanelTask(null);
    dispatch(fetchTasks({ projectId, pageSize: 5000 }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'backlog') {
      navigate(`/projects/${projectId}`);
    } else if (tab === 'board') {
      navigate(`/projects/${projectId}/board`);
    } else if (tab === 'knowledge') {
      navigate(`/projects/${projectId}/knowledge`);
    } else if (tab === 'settings') {
      navigate(`/projects/${projectId}/settings`);
    } else if (tab === 'allwork') {
      navigate(`/projects/${projectId}/allwork`);
    } else if (tab === 'allissues') {
      navigate(`/projects/${projectId}/allissues`);
    } else if (tab === 'executive') {
      navigate(`/projects/${projectId}/executive-plan`);
    }
  };

  const STATUS_CONFIG = {
    active: { label: 'Active', class: 'badge-success', banner: null },
    on_hold: { label: 'On Hold', class: 'badge-warning', banner: 'This project is currently on hold. Sprint creation is disabled.', bannerType: 'warning' },
    completed: { label: 'Completed', class: 'badge-info', banner: 'This project has been completed. Creating new tickets is restricted.', bannerType: 'info' },
    archived: { label: 'Archived', class: 'badge-secondary', banner: 'This project is archived and read-only. No modifications are allowed.', bannerType: 'danger' }
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || { label: status, class: 'badge-primary', banner: null };
  };

  if (loading && !currentProject) {
    return (
      <div className="loading-state">
        <span className="spinner"></span>
        <p>Loading project...</p>
      </div>
    );
  }

  if (!currentProject) {
    return <div className="error-state">Project not found</div>;
  }

  return (
    <div className="project-page">
      {/* Project Header */}
      <div className="project-page-header">
        <div className="project-page-header-content">
          <div className="breadcrumbs">
            <Link to="/projects">Projects</Link>
            <span>/</span>
            <span className="current">{currentProject.name}</span>
          </div>
          <div className="project-title-row">
            <span className="project-key-badge">{currentProject.key}</span>
            <h1 className="project-title">{currentProject.name}</h1>
            <span className={`badge ${getStatusConfig(currentProject.status).class}`}>
              {getStatusConfig(currentProject.status).label}
            </span>
          </div>
        </div>
        {canGenerateFullReport && (
          <div className="project-page-header-actions" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            {canGenerateFullReport && (
              <button
                className="btn btn-secondary"
                style={{ marginTop: '8px' }}
                onClick={() => {
                  setReportScope('project');
                  setReportSprintId('');
                  setReportStartDate('');
                  setReportEndDate('');
                  setShowReportModal(true);
                }}
              >
                Generate Report
              </button>
            )}
          </div>
        )}
      </div>

      {/* Status Banner */}
      {getStatusConfig(currentProject.status).banner && (
        <div className={`project-status-banner ${getStatusConfig(currentProject.status).bannerType}`}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{getStatusConfig(currentProject.status).banner}</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="project-tabs">
        <div className="project-tabs-container">
          <button
            className={`project-tab ${activeTab === 'backlog' ? 'active' : ''}`}
            onClick={() => handleTabChange('backlog')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 3.5A1.5 1.5 0 013.5 2h9A1.5 1.5 0 0114 3.5v9a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 012 12.5v-9zM3.5 3a.5.5 0 00-.5.5v9a.5.5 0 00.5.5h9a.5.5 0 00.5-.5v-9a.5.5 0 00-.5-.5h-9z" />
              <path d="M4 5.5a.5.5 0 01.5-.5h7a.5.5 0 010 1h-7a.5.5 0 01-.5-.5zm0 3a.5.5 0 01.5-.5h7a.5.5 0 010 1h-7a.5.5 0 01-.5-.5zm0 3a.5.5 0 01.5-.5h4a.5.5 0 010 1h-4a.5.5 0 01-.5-.5z" />
            </svg>
            Backlog
          </button>
          <button
            className={`project-tab ${activeTab === 'board' ? 'active' : ''}`}
            onClick={() => handleTabChange('board')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M0 2a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1v7.5a2.5 2.5 0 01-2.5 2.5h-9A2.5 2.5 0 011 12.5V5a1 1 0 01-1-1V2zm2 3v7.5A1.5 1.5 0 003.5 14h9a1.5 1.5 0 001.5-1.5V5H2zm13-3H1v2h14V2zM5 7.5a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5a.5.5 0 01-.5-.5z" />
            </svg>
            Active Sprint
            {activeSprint && (
              <span className="tab-badge">{activeSprint.name}</span>
            )}
          </button>
          <button
            className={`project-tab ${activeTab === 'knowledge' ? 'active' : ''}`}
            onClick={() => handleTabChange('knowledge')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 000 2.5v11a.5.5 0 00.707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 00.78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0016 13.5v-11a.5.5 0 00-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z" />
            </svg>
            Knowledge Center
          </button>
          <button
            className={`project-tab ${activeTab === 'allwork' ? 'active' : ''}`}
            onClick={() => handleTabChange('allwork')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1.5 1a.5.5 0 00-.5.5v3a.5.5 0 001 0V2h3.5a.5.5 0 000-1H1.5zM14.5 1H11a.5.5 0 000 1h3v2.5a.5.5 0 001 0v-3a.5.5 0 00-.5-.5zM1 10.5a.5.5 0 011 0V13h2.5a.5.5 0 010 1H1.5a.5.5 0 01-.5-.5v-3zm13 0a.5.5 0 00-1 0V13h-2.5a.5.5 0 000 1h3a.5.5 0 00.5-.5v-3z"/>
              <path d="M3 5.5a.5.5 0 01.5-.5h9a.5.5 0 010 1h-9a.5.5 0 01-.5-.5zm0 3a.5.5 0 01.5-.5h9a.5.5 0 010 1h-9a.5.5 0 01-.5-.5zm0 3a.5.5 0 01.5-.5h9a.5.5 0 010 1h-9a.5.5 0 01-.5-.5z"/>
            </svg>
            Completed Sprints
          </button>

          <button
            className={`project-tab ${activeTab === 'allissues' ? 'active' : ''}`}
            onClick={() => handleTabChange('allissues')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1.5 1a.5.5 0 00-.5.5v13a.5.5 0 00.5.5h13a.5.5 0 00.5-.5v-13a.5.5 0 00-.5-.5h-13zm1 1h11v11h-11V2z"/>
              <path d="M3 5h10v1H3V5zm0 3h10v1H3V8zm0 3h7v1H3v-1z"/>
            </svg>
            All Issues
          </button>

          <button
            className={`project-tab ${activeTab === 'executive' ? 'active' : ''}`}
            onClick={() => handleTabChange('executive')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm1 8a1 1 0 100 2h6a1 1 0 100-2H7zm0 4a1 1 0 100 2h3a1 1 0 100-2H7z"/>
            </svg>
            Executive Plan
          </button>

          {canViewSettings && (
            <button
              className={`project-tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => handleTabChange('settings')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M11.5 2a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM9.05 3a2.5 2.5 0 014.9 0H16v1h-2.05a2.5 2.5 0 01-4.9 0H0V3h9.05zM4.5 7a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM2.05 8a2.5 2.5 0 014.9 0H16v1H6.95a2.5 2.5 0 01-4.9 0H0V8h2.05zm9.45 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm-2.45 1a2.5 2.5 0 014.9 0H16v1h-2.05a2.5 2.5 0 01-4.9 0H0v-1h9.05z" />
              </svg>
              Settings
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="project-tab-content">
        {activeTab === 'backlog' && (
          <ProjectBacklog
            projectId={projectId}
            members={members}
            canManage={canManageProject}
            onOpenTask={handleOpenTaskSidePanel}
          />
        )}
        {activeTab === 'board' && (
          <ProjectBoard
            projectId={projectId}
            embedded={true}
            onOpenTask={handleOpenTaskSidePanel}
          />
        )}
        {activeTab === 'allwork' && (
          <AllWork
            projectId={projectId}
          />
        )}
        {activeTab === 'allissues' && (
          <AllIssues
            projectId={projectId}
          />
        )}
        {activeTab === 'knowledge' && (
          <KnowledgeCenter
            projectId={projectId}
            members={members}
            canManage={canManageProject}
          />
        )}
        {activeTab === 'executive' && (
          <ExecutivePlan projectId={projectId} />
        )}
        {activeTab === 'settings' && canViewSettings && (
          <ProjectSettings
            projectId={projectId}
            members={members}
            canManage={canManageProject}
          />
        )}
      </div>
      
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Generate Project Report"
        size="small"
      >
        <div className="form-group">
          <label className="form-label">Scope</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                value="project"
                checked={reportScope === 'project'}
                onChange={() => setReportScope('project')}
              />
              Entire project
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="sprint"
                checked={reportScope === 'sprint'}
                onChange={() => setReportScope('sprint')}
              />
              Specific sprint
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="date_range"
                checked={reportScope === 'date_range'}
                onChange={() => setReportScope('date_range')}
              />
              Custom period
            </label>
          </div>
        </div>

        {reportScope === 'sprint' && (
          <div className="form-group">
            <label className="form-label">Sprint</label>
            <select
              className="form-select"
              value={reportSprintId}
              onChange={(e) => setReportSprintId(e.target.value)}
            >
              <option value="">Select a sprint...</option>
              {sprints.map((sprint) => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.name} ({sprint.status})
                </option>
              ))}
            </select>
          </div>
        )}

        {reportScope === 'date_range' && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start date</label>
              <input
                type="date"
                className="form-input"
                value={reportStartDate}
                onChange={(e) => setReportStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">End date</label>
              <input
                type="date"
                className="form-input"
                value={reportEndDate}
                onChange={(e) => setReportEndDate(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={() => setShowReportModal(false)}
            disabled={reportGenerating}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={
              reportGenerating ||
              (reportScope === 'sprint' && !reportSprintId)
            }
            onClick={async () => {
              try {
                setReportGenerating(true);
                let startIso = null;
                let endIso = null;
                if (reportScope === 'date_range') {
                  startIso = reportStartDate
                    ? new Date(`${reportStartDate}T00:00:00Z`).toISOString()
                    : null;
                  endIso = reportEndDate
                    ? new Date(`${reportEndDate}T23:59:59Z`).toISOString()
                    : null;
                }

                await generateAndDownloadReport({
                  projectId,
                  scope: reportScope,
                  sprintId:
                    reportScope === 'sprint' && reportSprintId
                      ? parseInt(reportSprintId, 10)
                      : null,
                  startDateIso: startIso,
                  endDateIso: endIso,
                  setLoading: () => {},
                });
                toast.success('Project report generated successfully.');
                setShowReportModal(false);
              } catch (error) {
                toast.error(
                  error?.response?.data?.detail ||
                    'Failed to generate project report.'
                );
              } finally {
                setReportGenerating(false);
              }
            }}
          >
            {reportGenerating ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </Modal>

      {/* Inline Side Panel — renders on top of backlog/board without navigating away */}
      {sidePanelTask && (
        <TaskDetailSidePanel
          identifier={sidePanelTask}
          identifierType={isNaN(sidePanelTask) ? 'key' : 'id'}
          onClose={handleCloseSidePanel}
          onDeleted={handleTaskDeletedInPanel}
        />
      )}
    </div>
  );
};

export default ProjectPage;
